// src/components/settings/PayslipSettingsPage.js — Phase 4b
// Payslip PDF download format settings.
// Writes to companies/{companyId}/settings/payslips.

import { useState, useEffect } from 'react';
import Icon from "../icons/Icon";
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/components/settings-pages.css';
import '../../styles/sections/payslip-settings.css';

const DEFAULTS = { layout: '3x2', format: 'bw', paper: 'a4' };

const PayslipSettingsPage = () => {
  const { company, hasPermission } = useAuth();
  const canEdit = hasPermission('set:slips:edit');

  const [settings, setSettings] = useState(DEFAULTS);
  const [saved, setSaved] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!company?.id) return;
    (async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, 'companies', company.id, 'settings', 'payslips'));
        const data = snap.exists() ? { ...DEFAULTS, ...snap.data() } : DEFAULTS;
        setSettings(data);
        setSaved(data);
      } catch (err) {
        console.error('[PayslipSettingsPage] fetch error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [company?.id]);

  const hasChanges =
    settings.layout !== saved.layout ||
    settings.format !== saved.format ||
    settings.paper  !== saved.paper;

  const handleDiscard = () => setSettings(saved);

  const handleSave = async () => {
    if (!company?.id || !hasChanges) return;
    try {
      setSaving(true);
      await setDoc(
        doc(db, 'companies', company.id, 'settings', 'payslips'),
        { layout: settings.layout, format: settings.format, paper: settings.paper },
        { merge: true }
      );
      setSaved(settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      console.error('[PayslipSettingsPage] save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => {
    if (!canEdit) return; // read-only mode — ignore all mutations
    setSettings((s) => ({ ...s, [key]: val }));
  };

  if (loading) {
    return (
      <div>
        <div className="settings-loading">
          <div className="settings-loading-spinner" />
          <div className="settings-loading-text">Loading payslip settings…</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="settings-page-title">Payslips</h1>
      <p className="settings-page-subtitle">
        Configure the layout and print format for downloaded payslip PDFs.
      </p>

      {/* ── Layout ─────────────────────────────────────────────── */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon">
              <span role="img" aria-label="layout">⊞</span>
            </div>
            <div>
              <h2 className="settings-card-title">Compact Bulk Layout</h2>
              <p className="settings-card-description">
                Number of payslips per page when using Download All
              </p>
            </div>
          </div>
        </div>
        <div className="settings-card-body">
          <div className="pss-option-group pss-option-group--row">

            <div
              className={`pss-option-card${settings.layout === '3x2' ? ' pss-option-card--selected' : ''}`}
              onClick={() => set('layout', '3x2')}
            >
              <div className="pss-option-radio">
                <div className={`pss-radio-dot${settings.layout === '3x2' ? ' pss-radio-dot--on' : ''}`} />
              </div>
              <div className="pss-option-body">
                <div className="pss-option-label">3×2 — 6 per page</div>
                <div className="pss-option-hint">3 columns, 2 rows per page</div>
              </div>
              <div className="pss-paper-preview">
                <div className="pss-paper-grid pss-paper-grid--3x2">
                  {Array.from({ length: 6 }).map((_, i) => <div key={i} className="pss-grid-cell" />)}
                </div>
              </div>
            </div>

            <div
              className={`pss-option-card${settings.layout === '2x2' ? ' pss-option-card--selected' : ''}`}
              onClick={() => set('layout', '2x2')}
            >
              <div className="pss-option-radio">
                <div className={`pss-radio-dot${settings.layout === '2x2' ? ' pss-radio-dot--on' : ''}`} />
              </div>
              <div className="pss-option-body">
                <div className="pss-option-label">2×2 — 4 per page</div>
                <div className="pss-option-hint">2 columns, 2 rows per page</div>
              </div>
              <div className="pss-paper-preview">
                <div className="pss-paper-grid pss-paper-grid--2x2">
                  {Array.from({ length: 4 }).map((_, i) => <div key={i} className="pss-grid-cell" />)}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Paper size ─────────────────────────────────────────── */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon">
              <span role="img" aria-label="paper"><Icon name="file-text" size={16} /></span>
            </div>
            <div>
              <h2 className="settings-card-title">Paper Size</h2>
              <p className="settings-card-description">
                Paper size for compact bulk downloads. Individual payslips are always A4.
              </p>
            </div>
          </div>
        </div>
        <div className="settings-card-body">
          <div className="pss-option-group pss-option-group--row pss-option-group--3">

            {[
              { val: 'a4',    label: 'A4',              dims: '210 × 297 mm (8.27" × 11.69")' },
              { val: 'short', label: 'Short Bond Paper', dims: '215.9 × 279.4 mm (8.5" × 11")' },
              { val: 'long',  label: 'Long Bond Paper',  dims: '215.9 × 330.2 mm (8.5" × 13")' },
            ].map(({ val, label, dims }) => (
              <div
                key={val}
                className={`pss-option-card${settings.paper === val ? ' pss-option-card--selected' : ''}`}
                onClick={() => set('paper', val)}
              >
                <div className="pss-option-radio">
                  <div className={`pss-radio-dot${settings.paper === val ? ' pss-radio-dot--on' : ''}`} />
                </div>
                <div className="pss-option-body">
                  <div className="pss-option-label">{label}</div>
                  <div className="pss-option-hint">{dims}</div>
                </div>
              </div>
            ))}

          </div>
        </div>
      </div>

      {/* ── Print format ───────────────────────────────────────── */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon">
              <span role="img" aria-label="print"><Icon name="printer" size={16} /></span>
            </div>
            <div>
              <h2 className="settings-card-title">Print Format</h2>
              <p className="settings-card-description">
                Color scheme for downloaded PDFs — applies to both individual and bulk downloads
              </p>
            </div>
          </div>
        </div>
        <div className="settings-card-body">
          <div className="pss-option-group pss-option-group--row">

            <div
              className={`pss-option-card${settings.format === 'bw' ? ' pss-option-card--selected' : ''}`}
              onClick={() => set('format', 'bw')}
            >
              <div className="pss-option-radio">
                <div className={`pss-radio-dot${settings.format === 'bw' ? ' pss-radio-dot--on' : ''}`} />
              </div>
              <div className="pss-option-body">
                <div className="pss-option-label">Ink-Friendly</div>
                <div className="pss-option-hint">No colored fills. All text in black.</div>
              </div>
              <div className="pss-format-preview pss-format-preview--bw">
                <div className="pss-fp-header" />
                <div className="pss-fp-lines">
                  <div className="pss-fp-line" />
                  <div className="pss-fp-line pss-fp-line--short" />
                  <div className="pss-fp-line" />
                  <div className="pss-fp-line pss-fp-line--short" />
                </div>
                <div className="pss-fp-net" />
              </div>
            </div>

            <div
              className={`pss-option-card${settings.format === 'colored' ? ' pss-option-card--selected' : ''}`}
              onClick={() => set('format', 'colored')}
            >
              <div className="pss-option-radio">
                <div className={`pss-radio-dot${settings.format === 'colored' ? ' pss-radio-dot--on' : ''}`} />
              </div>
              <div className="pss-option-body">
                <div className="pss-option-label">Colored</div>
                <div className="pss-option-hint">Emerald header, colored amounts. Requires a color printer.</div>
              </div>
              <div className="pss-format-preview pss-format-preview--colored">
                <div className="pss-fp-header" />
                <div className="pss-fp-lines">
                  <div className="pss-fp-line" />
                  <div className="pss-fp-line pss-fp-line--short" />
                  <div className="pss-fp-line" />
                  <div className="pss-fp-line pss-fp-line--short" />
                </div>
                <div className="pss-fp-net" />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Floating save bar ──────────────────────────────────── */}
      <div className={`settings-floating-save-bar${canEdit && (hasChanges || saveSuccess) ? ' visible' : ''}${saveSuccess ? ' saved' : ''}`}>
        <div className="settings-save-bar-content">
          <div className={`settings-save-bar-left${saveSuccess ? ' success' : ''}`}>
            <span className="settings-save-bar-dot" />
            <span className="settings-save-bar-check"><Icon name="check" size={14} /></span>
            <span className="settings-save-bar-text">
              {saveSuccess ? 'Changes saved' : 'Unsaved changes'}
            </span>
          </div>
          {!saveSuccess && (
            <div className="settings-save-bar-actions">
              <button className="settings-btn settings-btn-secondary" onClick={handleDiscard} disabled={saving}>
                Discard
              </button>
              <button className="settings-btn settings-btn-primary" onClick={handleSave} disabled={!hasChanges || saving}>
                {saving ? <><span className="settings-btn-spinner" />Saving…</> : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayslipSettingsPage;
